import { memoize } from 'es-toolkit/compat';
import { faker } from '@faker-js/faker';

export type ServiceInfo = {
    name: string;
    version: string;
};

export type Activity = 'available' | 'resting' | 'working' | 'driving';
export type UserState = 'success' | 'warning' | 'danger';

export type UserActivity = {
    percent: number;
    activity: Activity;
};

export type User = {
    itemId: string;
    userAvatar: string;
    userStatus: UserState;
    userNickName: string;
    userFirstName: string;
    userLastName: string;
    userActivity: UserActivity;
    userJobTitle: string;
    userEmail: string;
    userPhoneNumber: string;
};

export type TransformedUser = User & {
    userActivityLabel: string;
    userActivityValue: number;
    dataKey: string;
};

export type ColumnKey =
    | 'itemId'
    | 'userImageStatus'
    | 'userNickName'
    | 'userFirstName'
    | 'userLastName'
    | 'userActivity'
    | 'userJobTitle'
    | 'userEmail'
    | 'userPhoneNumber';

export type ColumnDetails = {
    resizeMaxWidth?: number;
    resizeMinWidth?: number;
    width?: number | string;
};

export type ColumnDetailsMap = Record<string, ColumnDetails>;

export type ColumnsConfig = {
    columnOrder: ColumnKey[];
    hiddenColumns: ColumnKey[];
    columnsDetails: ColumnDetailsMap;
};

export type TableConfig = {
    defaultColumnOrder: ColumnKey[];
    defaultHiddenColumns: ColumnKey[];
    defaultColumnsDetails: ColumnDetailsMap;
    disabledColumns: ColumnKey[];
    columnLabels: Record<ColumnKey, string>;
    rawSortKeys: ColumnKey[];
    sortKeyReplacement: Partial<Record<ColumnKey, keyof TransformedUser>>;
    defaultSortKey: ColumnKey;
    TABLE_CHUNK_SIZE: number;
};

export const serviceInfo: ServiceInfo = {
    name: 'Demo service',
    version: '0.0.1',
};

export const BASE_PATH: string = '/demoService';

export const activities: Activity[] = ['available', 'resting', 'working', 'driving'];
export const userStates: UserState[] = ['success', 'warning', 'danger'];
const DEMO_ROW_COUNT = 250;

export const data: User[] = Array.from({ length: DEMO_ROW_COUNT }, (_, index) => ({
    itemId: `${index + 1}`,
    userAvatar: faker.image.personPortrait(),
    userStatus: faker.helpers.arrayElement(userStates),
    userNickName: faker.internet.username(),
    userFirstName: faker.person.firstName(),
    userLastName: faker.person.lastName(),
    userActivity: {
        percent: faker.number.int({ max: 100 }),
        activity: faker.helpers.arrayElement(activities),
    },
    userJobTitle: faker.person.jobTitle(),
    userEmail: faker.internet.email(),
    userPhoneNumber: faker.phone.number(),
}));

// Transform data in order to:
// - Translate item props as we search and sort accordingly with respect to translations or composed values
// - format values as they might be relevant for search
export const transformData: (items: User[]) => TransformedUser[] = memoize((items: User[]) =>
    items.map(item => ({
        ...item,
        userActivityLabel: `${item.userActivity.percent} min`,
        userActivityValue: item.userActivity.percent,
        dataKey: item.itemId,
    }))
);

const defaultColumnOrder: ColumnKey[] = [
    'itemId',
    'userImageStatus',
    'userNickName',
    'userFirstName',
    'userLastName',
    'userActivity',
    'userJobTitle',
    'userEmail',
    'userPhoneNumber',
];

const disabledColumns: ColumnKey[] = ['userFirstName'];

const defaultHiddenColumns: ColumnKey[] = ['itemId'];

const defaultSortKey: ColumnKey = 'userNickName';

const columnLabels: Record<ColumnKey, string> = {
    itemId: 'Id',
    userImageStatus: 'User',
    userNickName: 'Nickname',
    userFirstName: 'First name',
    userLastName: 'Last name',
    userActivity: 'Activity',
    userJobTitle: 'Job',
    userEmail: 'Email',
    userPhoneNumber: 'Phone',
};

const defaultColumnsDetails: ColumnDetailsMap = {
    itemId: {},
    userImageStatus: {
        resizeMaxWidth: 100,
        width: 70,
    },
    userNickName: {},
    userFirstName: {},
    userLastName: {},
    userActivity: {
        width: 200,
    },
    userJobTitle: {},
    userEmail: {},
    userPhoneNumber: {},
};

const rawSortKeys: ColumnKey[] = [];

const sortKeyReplacement: Partial<Record<ColumnKey, keyof TransformedUser>> = {
    userActivity: 'userActivityLabel',
    userImageStatus: 'userStatus',
};

export const tableConfig: TableConfig = {
    defaultColumnOrder,
    defaultHiddenColumns,
    defaultColumnsDetails,
    disabledColumns,
    columnLabels,
    rawSortKeys,
    sortKeyReplacement,
    defaultSortKey,
    TABLE_CHUNK_SIZE: 50,
};
